import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useAuth } from "../../Context/authContext";
import { Typography } from "../../Components/Typography";
import Table from "../../Components/Table";
import Button from "../../Components/Button";
import TextField from "../../Components/TextField";
import {
  getAllTransactions,
  getPaymentReceipt,
  createDonationCheckoutSession,
} from "../../Utils/apiServices";
import { parseListFromApiResponse } from "../../Utils/parseApiListResponse";
import toast from "../../Utils/toastManager";

const MIN_DONATION_USD = 1;

const formatCurrency = (amount) => {
  const value = Number(amount || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number.isNaN(value) ? 0 : value);
};

const normalizeComparableId = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

/** /transactions is unscoped for staff; show every row. Donors match their own id/email. */
const seesAllPlatformTransactions = (role) =>
  role === "admin" || role === "program_team";

const rowEmail = (item) =>
  (item?.user?.email || item?.user_data?.customerEmail || "")
    .trim()
    .toLowerCase();

const fullNameFromProfile = (profile) => {
  if (!profile || typeof profile !== "object") return "";
  return [profile.first_name, profile.last_name]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ");
};

/** Prefer `user`, then Stripe snapshot `user_data`, then email. */
const transactionDonorName = (record) => {
  const named =
    fullNameFromProfile(record?.user) || fullNameFromProfile(record?.user_data);
  const email = (record?.user?.email || record?.user_data?.customerEmail || "").trim();
  return named || email || "-";
};

/** Match API row to current user by id and/or email (auth user may omit or mis-shape id). */
const transactionBelongsToUser = (item, user) => {
  const authId = normalizeComparableId(user?.id ?? user?.user_id);
  const authEmail = (user?.email || "").trim().toLowerCase();
  if (authId && normalizeComparableId(item?.user_id) === authId) return true;
  if (authEmail && rowEmail(item) === authEmail) return true;
  return false;
};

const DonorDonations = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiptLoadingId, setReceiptLoadingId] = useState("");
  const [certificateLoadingId, setCertificateLoadingId] = useState("");
  const [donationAmount, setDonationAmount] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const unwrapCheckoutPayload = (response) => {
    const body = response?.data;
    if (body && typeof body === "object" && body.data != null && typeof body.data === "object") {
      return body.data;
    }
    return body;
  };

  const handlePayNow = async () => {
    const email = (user?.email || "").trim();
    const rawId = user?.id ?? user?.user_id;
    const uid = typeof rawId === "number" && !Number.isNaN(rawId) ? rawId : Number(rawId);
    const amountNum = Number.parseFloat(String(donationAmount).trim());

    if (!email) {
      toast.warning("Your profile has no email. Please update your account and try again.");
      return;
    }
    if (rawId === null || rawId === undefined || rawId === "" || Number.isNaN(uid)) {
      toast.warning("Your account ID is missing. Please sign out and sign in again.");
      return;
    }
    if (!Number.isFinite(amountNum) || amountNum < MIN_DONATION_USD) {
      toast.warning(`Minimum donation amount is USD ${MIN_DONATION_USD}.`);
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await createDonationCheckoutSession({
        customerEmail: email,
        amount: amountNum,
        user_id: uid,
      });
      const payload = unwrapCheckoutPayload(response);
      const nested = payload?.data && typeof payload.data === "object" ? payload.data : null;
      const url = payload?.url || nested?.url;
      const ok = response?.success === true && payload?.success !== false && url;

      if (ok) {
        window.location.assign(url);
        return;
      }
      toast.error("Could not start checkout. Please try again.");
    } catch {
      toast.error("Could not start checkout. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const response = await getAllTransactions({
          role: user?.role,
          userId: user?.id ?? user?.user_id,
        });
        if (cancelled) return;
        const transactions = parseListFromApiResponse(response);
        const sortByCreatedAt = (a, b) =>
          new Date(b?.created_at || 0) - new Date(a?.created_at || 0);
        let myTransactions;
        if (seesAllPlatformTransactions(user?.role)) {
          myTransactions = [...transactions].sort(sortByCreatedAt);
        } else {
          myTransactions = transactions
            .filter((item) => transactionBelongsToUser(item, user))
            .sort(sortByCreatedAt);
          // Donor /transactions is scoped by token; if id/email in session do not match API fields, still show rows.
          if (myTransactions.length === 0 && transactions.length > 0) {
            myTransactions = [...transactions].sort(sortByCreatedAt);
          }
        }
        setRows(myTransactions);
      } catch (error) {
        if (!cancelled) {
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTransactions();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.user_id, user?.role]);

  const handleDownloadReceipt = async (transaction) => {
    if (!transaction?.transaction_id) return;
    setReceiptLoadingId(transaction.transaction_id);
    try {
      const response = await getPaymentReceipt(transaction.transaction_id);
      const payload = response?.data;
      const inner = payload && typeof payload === "object" && payload.data != null ? payload.data : payload;
      const receiptUrl =
        inner?.receiptUrl ||
        inner?.receipt_url ||
        inner?.url ||
        payload?.receiptUrl ||
        payload?.receipt_url ||
        payload?.url;
      if (receiptUrl) {
        window.open(receiptUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.warning("Receipt URL is not available for this transaction.");
      }
    } catch (error) {
      toast.error("Failed to fetch receipt. Please try again.");
    } finally {
      setReceiptLoadingId("");
    }
  };

  const handleDownloadCertificate = async (transaction) => {
    if (!transaction?.id) return;
    setCertificateLoadingId(transaction.id);
    let certificateContainer;
    try {
      const profile = transaction?.user || transaction?.user_data || {};
      const donorName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || "Valued Donor";
      const donorEmail = profile?.email || profile?.customerEmail || "-";
      const donorLocation = [profile?.city, profile?.state, profile?.country].filter(Boolean).join(", ") || "-";
      const donationAmount = formatCurrency(transaction?.amount);
      const donationDate = transaction?.created_at
        ? new Date(transaction.created_at).toLocaleString()
        : "-";

      certificateContainer = document.createElement("div");
      certificateContainer.style.position = "fixed";
      certificateContainer.style.left = "-99999px";
      certificateContainer.style.top = "0";
      certificateContainer.style.width = "1120px";
      certificateContainer.style.backgroundColor = "#ffffff";
      certificateContainer.style.padding = "40px";
      certificateContainer.innerHTML = `
        <div style="font-family: Arial, sans-serif; width: 100%; border: 8px solid #0f6f78; padding: 36px; box-sizing: border-box;">
          <div style="text-align: center;">
            <div style="font-size: 40px; font-weight: 700; color: #0f6f78;">IgniteNext</div>
            <div style="font-size: 28px; margin-top: 8px; color: #1f2937; font-weight: 700;">Certificate of Appreciation</div>
            <div style="font-size: 16px; margin-top: 16px; color: #4b5563;">This certificate is proudly presented to</div>
            <div style="font-size: 34px; margin-top: 10px; color: #111827; font-weight: 700; text-transform: capitalize;">${donorName}</div>
          </div>

          <div style="margin-top: 24px; text-align: center; color: #374151; font-size: 18px; line-height: 1.6;">
            In recognition of your generous donation and support for the IgniteNext mission.
          </div>

          <div style="margin-top: 30px; border: 1px solid #d1d5db; border-radius: 10px; padding: 18px 22px;">
            <div style="font-size: 16px; margin-bottom: 8px;"><strong>Donor:</strong> ${donorName}</div>
            <div style="font-size: 16px; margin-bottom: 8px;"><strong>Email:</strong> ${donorEmail}</div>
            <div style="font-size: 16px; margin-bottom: 8px;"><strong>Location:</strong> ${donorLocation}</div>
            <div style="font-size: 16px; margin-bottom: 8px;"><strong>Donation Amount:</strong> ${donationAmount}</div>
            <div style="font-size: 16px; margin-bottom: 8px;"><strong>Date:</strong> ${donationDate}</div>
            <div style="font-size: 16px;"><strong>Transaction Reference:</strong> ${transaction?.transaction_id || "-"}</div>
          </div>

          <div style="margin-top: 30px; display: flex; justify-content: space-between; align-items: flex-end;">
            <div style="font-size: 14px; color: #6b7280;">
              Thank you for helping us build a stronger innovation community.
            </div>
            <div style="text-align: center;">
              <div style="width: 160px; border-top: 1px solid #111827; margin-bottom: 6px;"></div>
              <div style="font-size: 14px; color: #1f2937;">IgniteNext Team</div>
            </div>
          </div>
        </div>
      `;

      document.body.appendChild(certificateContainer);

      const canvas = await html2canvas(certificateContainer, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imageData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("landscape", "pt", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(imageData, "PNG", 0, 0, pageWidth, pageHeight);
      const safeName = donorName.replace(/[^a-z0-9]/gi, "_").toLowerCase();
      pdf.save(`ignitenext_certificate_${safeName || "donor"}.pdf`);

    } catch (error) {
      toast.error("Failed to generate certificate. Please try again.");
    } finally {
      if (certificateContainer && document.body.contains(certificateContainer)) {
        document.body.removeChild(certificateContainer);
      }
      setCertificateLoadingId("");
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Name",
        key: "donation_donor_name",
        accessor: (row) => transactionDonorName(row),
        sortable: true,
        render: (value) => (
          <Typography as="span" className="text-sm text-primary-900 capitalize">
            {value}
          </Typography>
        ),
      },
      {
        header: "Amount",
        accessor: "amount",
        sortable: true,
        render: (value) => (
          <Typography as="span" className="text-sm font-semibold text-primary-900">
            {formatCurrency(value)}
          </Typography>
        ),
      },
      {
        header: "Status",
        accessor: "status",
        sortable: true,
        render: (value) => {
          const isPaid = String(value || "").toLowerCase() === "paid";
          return (
            <span
              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                isPaid ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
              }`}
            >
              {value || "-"}
            </span>
          );
        },
      },
      {
        header: "Date",
        accessor: "created_at",
        sortable: true,
        render: (value) =>
          value ? new Date(value).toLocaleString() : "-",
      },
      {
        header: "Receipt",
        accessor: "receipt",
        sortable: false,
        render: (_, record) => (
          <Button
            variant="secondary"
            size="small"
            disabled={receiptLoadingId === record?.transaction_id}
            onClick={() => handleDownloadReceipt(record)}
          >
            {receiptLoadingId === record?.transaction_id
              ? "Loading..."
              : "Download receipt"}
          </Button>
        ),
      },
      {
        header: "Certificate",
        accessor: "certificate",
        sortable: false,
        render: (_, record) => (
          <Button
            variant="secondary"
            size="small"
            disabled={certificateLoadingId === record?.id}
            onClick={() => handleDownloadCertificate(record)}
          >
            {certificateLoadingId === record?.id
              ? "Loading..."
              : "Download certificate"}
          </Button>
        ),
      },
    ],
    [receiptLoadingId, certificateLoadingId]
  );

  return (
    <section className="flex flex-col w-full h-auto rounded-[40px] pt-8 pb-12 gap-6 bg-primary">
      <div className="px-8">
        <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <Typography as="p" className="text-xs text-tertiary-600">
            Uses your IgniteNext login email and account for Stripe checkout.
          </Typography>
          <div className="flex flex-col items-stretch gap-4 md:flex-row md:flex-wrap md:items-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                document.getElementById("donationAmount")?.focus();
              }}
              className="w-full shrink-0 md:w-auto"
            >
              Donate now
            </Button>
            <div className="min-w-0 flex-1 md:max-w-xs">
              <TextField
                name="donationAmount"
                type="number"
                label="Amount (USD)"
                placeholder="e.g. 25.00"
                value={donationAmount}
                onChange={(e) => setDonationAmount(e.target.value)}
                disabled={checkoutLoading}
                min={String(MIN_DONATION_USD)}
                step="0.01"
              />
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={handlePayNow}
              disabled={checkoutLoading}
              className="w-full md:w-auto md:shrink-0"
            >
              {checkoutLoading ? "Opening Stripe…" : "Pay now"}
            </Button>
          </div>
        </div>
      </div>

      <div className="px-8 flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-tertiary flex items-center justify-center">
          <Icon icon="mdi:hand-heart-outline" className="h-5 w-5 text-gray-600" />
        </div>
        <div>
          <Typography as="h2" className="text-xl font-semibold text-primary-900">
            Donation History
          </Typography>
          <Typography as="p" className="text-sm text-tertiary-600">
            {seesAllPlatformTransactions(user?.role)
              ? "All donations recorded on the platform."
              : "List of donations made by you."}
          </Typography>
        </div>
      </div>

      <div className="px-8">
        <Table
          columns={columns}
          data={rows}
          loading={loading}
          rowKey="id"
          emptyMessage="No donations found."
        />
      </div>
    </section>
  );
};

export default DonorDonations;
